import { Check, Hammer, Lightbulb, Wrench, Ruler, Package } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../data/mock';

const BespokeSetDesign = () => {
  const setDesignService = services[1];

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-red-600/10 flex items-center justify-center">
              <Hammer className="text-red-500" size={36} />
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-red-500 mb-6 uppercase">
            Bespoke Set Design & Construction
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            {setDesignService.description}
          </p>
        </div>

        {/* Hero Image */}
        <div className="max-w-6xl mx-auto mb-20">
          <img 
            src="https://images.unsplash.com/photo-1612544409025-e1f6a56c1152" 
            alt="Film set construction"
            className="w-full h-96 object-cover rounded-lg"
          />
        </div>

        {/* Services Overview */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Our Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Custom Set Design & Planning</h3>
                <p className="text-gray-400 text-sm">From initial concepts to detailed CAD drawings, we collaborate closely with you to design sets that perfectly match your creative vision and production requirements.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Full Construction Services</h3>
                <p className="text-gray-400 text-sm">Expert craftsmanship in building complete sets including walls, platforms, structural elements, and integrated practical lighting for film and photography.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Period-Specific Environments</h3>
                <p className="text-gray-400 text-sm">Authentic recreations from Victorian era to 1980s settings with meticulous attention to historical accuracy, architectural details, and period-appropriate materials.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Modular & Adaptable Builds</h3>
                <p className="text-gray-400 text-sm">Flexible set designs that can be reconfigured for multiple scenes, easily transported, and adapted to different shooting requirements and spatial constraints.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Set Dressing & Props</h3>
                <p className="text-gray-400 text-sm">Comprehensive styling services including curated furniture, decorative elements, props sourcing, and detailed dressing to bring your set to life with authentic atmosphere.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-12 text-center">Our Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-600/10 flex items-center justify-center">
                  <Lightbulb className="text-red-500" size={28} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">1. Concept & Design</h3>
              <p className="text-gray-400">Collaborative design consultation to understand your vision and narrative needs</p>
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-600/10 flex items-center justify-center">
                  <Ruler className="text-red-500" size={28} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">2. Planning & CAD</h3>
              <p className="text-gray-400">Detailed technical drawings, materials selection, and budget planning</p>
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-600/10 flex items-center justify-center">
                  <Wrench className="text-red-500" size={28} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">3. Construction</h3>
              <p className="text-gray-400">Expert craftsmanship bringing your set to life with precision and quality</p>
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-red-600/10 flex items-center justify-center">
                  <Package className="text-red-500" size={28} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">4. Installation</h3>
              <p className="text-gray-400">On-site installation, dressing, and final adjustments for your shoot</p>
            </div>
          </div>
        </div>

        {/* Set Types */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Set Types We Create</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <h3 className="text-xl font-semibold text-red-500 mb-4">Period & Historical Sets</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Authentic recreations from Victorian interiors to mid-century modern, with attention to architectural detail and period-accurate materials.
              </p>
              <ul className="text-gray-400 space-y-2 text-sm">
                <li>• 1920s-1980s period environments</li>
                <li>• Historical architectural details</li>
                <li>• Authentic fixtures and finishes</li>
              </ul>
            </div>

            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <h3 className="text-xl font-semibold text-red-500 mb-4">Contemporary & Modern</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Sleek, minimalist spaces perfect for fashion shoots, commercials, and modern storytelling with clean lines and sophisticated aesthetics.
              </p>
              <ul className="text-gray-400 space-y-2 text-sm">
                <li>• Minimalist interiors</li>
                <li>• High-end residential spaces</li>
                <li>• Corporate & office environments</li>
              </ul>
            </div>

            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <h3 className="text-xl font-semibold text-red-500 mb-4">Industrial & Underground</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Raw, atmospheric environments with exposed brick, metal framework, and moody lighting for edgy editorial and alternative productions.
              </p>
              <ul className="text-gray-400 space-y-2 text-sm">
                <li>• Warehouse aesthetics</li>
                <li>• Underground club environments</li>
                <li>• Urban decay & grunge</li>
              </ul>
            </div>

            <div className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <h3 className="text-xl font-semibold text-red-500 mb-4">Fantasy & Conceptual</h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                Imaginative, otherworldly environments limited only by creativity - from sci-fi interiors to dreamlike abstract spaces.
              </p>
              <ul className="text-gray-400 space-y-2 text-sm">
                <li>• Sci-fi & futuristic designs</li>
                <li>• Surreal & abstract environments</li>
                <li>• Custom thematic builds</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Capabilities */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Technical Capabilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <h3 className="text-2xl font-bold text-red-500 mb-2">Modular Design</h3>
              <p className="text-gray-400">Flexible, reconfigurable sets that adapt to different scenes and shooting requirements</p>
            </div>
            <div className="text-center bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <h3 className="text-2xl font-bold text-red-500 mb-2">Full Scale Builds</h3>
              <p className="text-gray-400">Complete room constructions with walls, ceilings, flooring, and integrated practical lighting</p>
            </div>
            <div className="text-center bg-zinc-900 rounded-lg p-8 border border-red-900/20">
              <h3 className="text-2xl font-bold text-red-500 mb-2">Prop & Dressing</h3>
              <p className="text-gray-400">Comprehensive set dressing services with curated props, furniture, and decorative elements</p>
            </div>
          </div>
        </div>

        {/* Gallery */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Previous Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_cinema-studio-3/artifacts/hyq486sk_image.png" 
              alt="Industrial fashion shoot with sequined styling"
              className="w-full h-80 object-cover rounded-lg"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_cinema-studio-3/artifacts/xu4u9h8l_image.png" 
              alt="Alternative gothic fashion industrial setting"
              className="w-full h-80 object-cover rounded-lg"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_cinema-studio-3/artifacts/e8fn2kxk_image.png" 
              alt="Industrial warehouse fashion editorial"
              className="w-full h-80 object-cover rounded-lg"
            />
          </div>
        </div>

        {/* Pricing */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Investment Packages</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {setDesignService.pricing.map((pkg, index) => (
              <div 
                key={index} 
                className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 hover:border-red-600/50 transition-all text-center"
              >
                <h3 className="text-xl font-semibold text-white mb-2">{pkg.package}</h3>
                <p className="text-4xl font-bold text-red-500 my-6">{pkg.price}</p>
                <Link 
                  to="/contact"
                  className="block w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
                >
                  {pkg.price === 'Contact' ? 'Discuss Project' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-gray-500 text-sm mt-8">
            Custom quotes available for large-scale productions and ongoing projects
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">Ready to Build Your Vision?</h2>
          <p className="text-gray-400 mb-8">Let's discuss your set design requirements</p>
          <Link 
            to="/contact"
            className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
          >
            Start Your Project
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BespokeSetDesign;
